import "jquery-ui/ui/widgets/sortable"
// @ts-expect-error: No definitions...
import Selectize from "selectize"
import {
  DropdownActionOption,
  LinkAction,
  ClipboardCopyAction,
  DropdownActionType,
  CustomAction,
} from "common/src/types/dropdown"
import {
  UnreachableError,
  SELECTIZE_DROPDOWN_CUSTOM_ACTION_EVENT_NAME,
} from "common/src/common"
import { buildOptionElement } from "./elements/selectize_dropdown"

declare global {
  namespace Selectize {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface IApi<T, U> {
      $control_input: JQuery<HTMLInputElement>
      $wrapper: JQuery<HTMLDivElement>
    }
  }
}

export type SelectizePlugin = "drag_drop" | "dropdown_action_options"

const DEFAULT_HEIGHT = 200

// Monkey patch the Selectize dropdown so that it's positioned above the input
// if the dropdown gets cut off at the bottom of the window. Refs:
// https://github.com/selectize/selectize.js/pull/1015/files
// https://github.com/selectize/selectize.js/issues/29#issuecomment-301207450
Selectize.prototype.positionDropdown = function (): void {
  this.$dropdown.css({ top: "auto" })
  const scrollParent = this.scrollParent()

  let parentBottom
  let parentTop
  if (scrollParent === document.documentElement) {
    parentBottom = scrollParent.clientHeight
    parentTop = 0
  } else {
    const scrollParentRect = scrollParent.getBoundingClientRect()
    parentBottom = scrollParentRect.bottom
    parentTop = scrollParentRect.top
  }

  let direction: "down" | "up"
  let alignment: string
  let maxHeight: number

  const rect = this.$control[0].getBoundingClientRect()
  const topSpaceAvailable = rect.top - parentTop - 5
  const bottomSpaceAvailable = parentBottom - rect.bottom - 5

  if (
    bottomSpaceAvailable >= DEFAULT_HEIGHT ||
    bottomSpaceAvailable >= topSpaceAvailable
  ) {
    direction = "down"
    maxHeight = bottomSpaceAvailable
  } else {
    direction = "up"
    maxHeight = topSpaceAvailable
  }

  if (maxHeight < 0 || maxHeight > DEFAULT_HEIGHT) {
    maxHeight = DEFAULT_HEIGHT
  }

  const selectizeDropdown = this.$dropdown.closest("selectize-dropdown")
  const placement = selectizeDropdown[0]?.getAttribute("placement") as
    | string
    | undefined
    | null

  if (placement !== undefined && placement !== null) {
    alignment = placement
  } else {
    // N.B. When testing to see if the dropdown will overflow off the page to the
    // right we check its parent's position because the dropdown itself may have
    // already been shifted due to this method having been previously ran.
    const dropdownParent = selectizeDropdown.parent() as JQuery | undefined
    const dropdownLeft = dropdownParent?.offset()?.left ?? 0
    const screenRight = document.documentElement.clientWidth
    if (dropdownLeft + this.$dropdown.outerWidth(true) > screenRight) {
      alignment = "right"
    } else {
      alignment = "left"
    }
  }

  this.setDropdownDirection(direction, alignment, maxHeight)
}

Selectize.prototype.setDropdownDirection = function (
  direction: "up" | "down",
  alignment: "left" | "right",
  maxHeight: number,
): void {
  const offset = this.$control.position()
  const rect = this.$control[0].getBoundingClientRect()

  // Note: $control is the input element, rect will be the size of the input element
  // Note: the size of the parent element currently == the size of the input element
  // Note: top, bottom, left, and right measurements are from the edge of the parent element

  this.$dropdown.css({
    // when dropdown opens downwards, attach it to the bottom edge of the input (N.B. positive=down)
    top: direction === "down" ? offset.top + rect.height : "auto",
    // when dropdown opens upwards, attach it to the top edge of the input (N.B. positive=up)
    bottom: direction === "up" ? rect.height - offset.top : "auto",
    // when left aligned, attach the dropdown to the left edge of the input, and then shift it right by the amount that the input is shifted right (N.B. positive=right)
    left: alignment === "left" ? offset.left : "auto",
    // when right aligned, attach the dropdown to the right edge of the input, and then shift it right by the amount that the input is shifted right (N.B. positive=left)
    right: alignment === "right" ? -offset.left : "auto",
  })

  this.$dropdown_content.css({
    "max-height": `${maxHeight}px`,
  })
}

// Grab nearest scrollable parent element. Bottoms out at <html> Ref:
// https://stackoverflow.com/a/42543908
// https://github.com/jquery/jquery-ui/blob/main/ui/scroll-parent.js
Selectize.prototype.scrollParent = function (): HTMLElement {
  let parent = this.$dropdown[0]
  // This can be undefined or empty string or element
  while (Boolean(parent.parentElement)) {
    parent = parent.parentElement
    const style = getComputedStyle(parent)
    if (
      style.overflow === "auto" ||
      style.overflow === "scroll" ||
      style.overflowY === "auto" ||
      style.overflowY === "scroll"
    ) {
      break
    }
  }

  return parent
}

// Temporary fix that adds support for disabling individual options in the
// dropdown. Selectize has been updated to support this but hasn't had a release
// that includes it yet. This should be removed once we update beyond 12.4
const originalRender = Selectize.prototype.render
Selectize.prototype.render = function (
  templateName: string,
  data: { disabled?: boolean; label?: string },
): HTMLElement {
  const html = originalRender.call(this, templateName, data)
  if (templateName === "option" && data.disabled === true) {
    html.removeAttribute("data-selectable")
    html.setAttribute("data-disabled", "")
  }

  // Fix to eliminate padding of optgroup headers which have no label.
  else if (templateName === "optgroup_header" && data.label === "") {
    html.classList.toggle("blank-optgroup-header", true)
  }

  return html
}

// Enable items to be removed from multi dropdowns by clicking their delete icon
const originalOnItemSelect = Selectize.prototype.onItemSelect
Selectize.prototype.onItemSelect = function (ev: MouseEvent): void {
  const target = ev.target as HTMLElement
  if (target.classList.contains("delete") && !(this.isDisabled as boolean)) {
    const value = target.closest("[data-value]")?.getAttribute("data-value")
    if (value) {
      ev.preventDefault()
      this.removeItem(value)
    }
  } else {
    originalOnItemSelect.call(this, ev)
  }
}

// Update the position of the dropdown, when the parent container is scrolled
{
  const originalSetup = Selectize.prototype.setup
  Selectize.prototype.setup = function (): void {
    // eslint-disable-next-line prefer-rest-params
    originalSetup.apply(this, arguments)

    const scrollParent = this.scrollParent()
    if (scrollParent !== document.documentElement) {
      scrollParent?.addEventListener("scroll", () => {
        this.positionDropdown()
      })
    }
  }
}

interface ActionOptionsPluginOpts {
  actionOptions: DropdownActionOption[]
}

Selectize.define(
  "dropdown_action_options",
  function (this: Selectize.Iapi, opts: ActionOptionsPluginOpts): void {
    const buildLink = (
      rootElement: HTMLElement,
      data: LinkAction,
    ): HTMLAnchorElement => {
      if (!data.href) {
        throw new Error("Href missing.")
      }

      const anchor = document.createElement("a")
      anchor.classList.add("selectize-dropdown-action-link")
      anchor.setAttribute("href", data.href)
      anchor.setAttribute("target", "_blank")
      anchor.setAttribute("rel", "noopener noreferrer")

      if (data.download === true) {
        anchor.setAttribute("download", "")
      }

      // If the click event is allowed to bubble up, Selectize seems to prevent
      // the default behavior so the link will not function as expected. Further
      // investigation could be useful here.
      anchor.addEventListener("click", event => {
        event.stopPropagation()
      })

      anchor.append(rootElement)
      return anchor
    }

    const buildClipboardCopy = (
      rootElement: HTMLElement,
      data: ClipboardCopyAction,
    ): HTMLElement => {
      if (!data.copyContent) {
        throw new Error("Copy content missing.")
      }

      const clipboardCopy = document.createElement("copy-to-clipboard")
      clipboardCopy.classList.add("selectize-dropdown-action-clipboard-copy")
      clipboardCopy.setAttribute("text", data.copyContent)

      clipboardCopy.append(rootElement)
      return clipboardCopy
    }

    const buildCustom = (rootElement: HTMLElement, data: CustomAction) => {
      const container = document.createElement("div")

      // Until Purview components have the ability to listen to custom events,
      // we use change events to trigger an action.
      const input = document.createElement("input")
      input.classList.add("v1-input")
      input.setAttribute("name", SELECTIZE_DROPDOWN_CUSTOM_ACTION_EVENT_NAME)
      input.setAttribute("value", data.value)
      input.setAttribute("style", "display: none;")
      rootElement.addEventListener("click", () => {
        input.dispatchEvent(new Event("change", { bubbles: true }))
      })

      container.append(rootElement, input)
      return container
    }

    const buildActionOption = (data: DropdownActionOption) => {
      const rootElement = buildOptionElement({ data })
      rootElement.setAttribute("data-selectable", "")

      switch (data.actionType) {
        case DropdownActionType.LINK:
          return buildLink(rootElement, data)
        case DropdownActionType.CLIPBOARD_COPY:
          return buildClipboardCopy(rootElement, data)
        case DropdownActionType.CUSTOM_ACTION:
          return buildCustom(rootElement, data)
        default:
          throw new UnreachableError("action type", data)
      }
    }

    const buildContainerElement = () => {
      const container = document.createElement("div")
      container.classList.add("selectize-dropdown-action-options")

      for (const data of opts.actionOptions) {
        const element = buildActionOption(data)
        container.appendChild(element)
      }

      return container
    }
    const originalSetup = this.setup
    this.setup = (() => {
      return () => {
        // eslint-disable-next-line prefer-rest-params
        originalSetup.apply(this, arguments)
        this.action_options = buildContainerElement()
        this.$dropdown[0].appendChild(this.action_options)
      }
    })()
  },
)
